import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

/**
 * Whether the mother has finished telling us who she is, and whether we may ask.
 *
 * Registration only ever collected an email and a password; her name, her stage
 * and her consents all came from the onboarding screen, which nothing navigated
 * to — so for every real account it never ran.
 *
 * The first fix kept "she skipped" in localStorage. That was wrong three ways:
 * a new phone or a reinstall made her look brand new and asked again; "not now"
 * and "no" were the same flag, so one skip silenced the prompt permanently and
 * the optional consents were lost; and a flag on a device cannot demonstrate to
 * a regulator who consented and when. It lives in the database now, beside the
 * consent it governs.
 */

export type SetupPromptState = 'never_asked' | 'deferred' | 'declined' | 'completed';

/** How many times a "not now" may be followed up. Two, then never again. */
const MAX_PROMPTS = 2;

export interface ProfileSetup {
  first_name: string | null;
  pregnancy_stage: string | null;
  consent_at: string | null;
  setup_prompt_state: SetupPromptState;
  setup_prompt_count: number;
}

export function useProfileSetup() {
  const { data: profile, isLoading } = useQuery<ProfileSetup>({
    queryKey: ['profile'],
    queryFn: () => api.get('/users/me/profile').then((r) => r.data),
  });

  // Stage is what "set up" means: care plan content and the companion's context
  // both key off it, so it changes what she is actually shown.
  const isComplete = Boolean(profile?.pregnancy_stage);
  const state = profile?.setup_prompt_state ?? 'never_asked';

  return {
    profile,
    isLoading,
    isComplete,
    state,
    /**
     * Intercept only when we know the answer, she is not done, she has not said
     * "don't ask again", and we have not already asked twice.
     *
     * When the profile is unknown — offline, expired token — this is false, so
     * a failed request can never trap her in a redirect.
     */
    shouldPrompt:
      !isLoading &&
      Boolean(profile) &&
      !isComplete &&
      state !== 'declined' &&
      (profile?.setup_prompt_count ?? 0) < MAX_PROMPTS,
  };
}

/**
 * Record her answer to the prompt.
 *
 * 'deferred' is "not now" — she may be asked again later, at a moment that is
 * not the middle of distress. 'declined' is "don't ask again" and is permanent;
 * after it, the only way in is the entry point she chooses herself in Profile.
 */
export function useSetupPromptResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (state: 'deferred' | 'declined') =>
      api.post('/users/me/setup-prompt', { state }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  });
}
